import React, {
  useMemo, useContext, useCallback, useState, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { compose } from 'recompose';

import useQuickSearch from '../../../hooks/useQuickSearch';
import { DateFormat } from '../../../utils/date';
import { MONEY_DECIMALS, formatCurrency } from '../../../utils/formatters';
import PayoutReconciliationContext from '../../context/PayoutReconciliationContext';
import InternalAssignmentContext, {
  withInternalAssignmentsContextProvider,
  withInternalAssignmentsContextReady,
} from '../../context/InternalAssignmentsContext';
import PayoutReconciliation from '../../Model/PayoutReconciliation';
import MonthSelect from '../MonthSelect';
import TableFilter from '../TableFilter';
import ColoredHeader from '../ColoredHeader';
import DownloadCSVButton from '../DownloadCSVButton';
import PageContent from '../PageContent';
import useCalculateCommissionByUser from '../../hooks/useCalculateCommissionByUser';
import texts from './texts.json';
import { getColumnsConfig } from './columns';
import { PayoutConfigByPreset, Preset, presetOptions } from './presets';
import {
  StyledAutoComplete,
  StyledTextField,
  StyledTabs,
  Container,
  FilterRow,
  TitleRow,
  Title,
  Description,
} from './styles';
import { commissionsTabs } from './utils';
import { commissionsTabsConfig } from './configs';

const LEAD_THRESHOLD = 65000;
const LEAD_PERCENTAGE = 0.01;
const COMMISSION_CURRENCY = 'usd';
const ALL = 'All';

const Commissions = ({ userId }) => {
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [payoutsConfig, setPayoutsConfig] = useState(null);
  const [selectedCoach, setSelectedCoach] = useState(ALL);
  const [coaches, setCoaches] = useState([]);
  const [selectedTab, setSelectedTab] = useState(commissionsTabs.COMMISSION_FULL_DATA);

  const {
    isUsers,
    crxUsers,
    camUsers,
    cpsUsers,
  } = useContext(InternalAssignmentContext);

  const [selectedUsers, setSelectedUsers] = useState([]);
  const userOptions = useMemo(() => {
    let userList;
    switch (selectedPreset?.id) {
      case Preset.INSIDE_SALES:
      case Preset.INSIDE_SALES_LEAD:
        userList = isUsers;
        break;
      case Preset.CRX:
      case Preset.CRX_LEAD:
        userList = crxUsers;
        break;
      case Preset.CAM:
        userList = camUsers;
        break;
      case Preset.CPS:
        userList = cpsUsers;
        break;
      default:
        userList = [];
    }

    return userList.map(({ userId: value, userName: label }) => ({ value, label }));
  }, [selectedPreset, isUsers, crxUsers, camUsers, cpsUsers]);

  const {
    payoutReconciliationCollection, loadPayoutReconciliationData, clearData, isLoadingDocs,
  } = useContext(
    PayoutReconciliationContext,
  );

  const onSelectedPresetChange = useCallback(
    (_, selectedOption) => {
      setSelectedUsers([]);
      setSelectedPreset(selectedOption);
      setPayoutsConfig(selectedOption?.id ? PayoutConfigByPreset[selectedOption.id] : null);
      setSelectedCoach(ALL);
      setCoaches([]);
      clearData();
    },
    [clearData],
  );

  const onSelectedTabChange = useCallback(
    (tab) => {
      setSelectedTab(tab);
      if (tab === commissionsTabs.COMMISSION_BY_USER
        && (selectedPreset?.id === Preset.INSIDE_SALES_LEAD || selectedPreset?.id === Preset.CRX_LEAD)) {
        clearData();
      }
    },
    [clearData, selectedPreset],
  );

  const filtersArray = useMemo(() => {
    /*
      We have to render the filters based on the preset selected. For CRX and CRX_LEAD, we have to render the CRX
      filter. The IS filter will be rendered if the preset INSIDE_SALES or INSIDE_SALES_LEAD is sslected.
    */
    const filters = [];

    // The preset is always rendered
    filters.push(
      <StyledAutoComplete
        onChange={onSelectedPresetChange}
        options={presetOptions}
        key={texts.preset}
        renderInput={(params) => <StyledTextField {...params} label={texts.preset} />}
      />,
    );

    // Render the month selector
    filters.push(<MonthSelect onChange={setSelectedDate} key={texts.monthSelect} />);

    // Render the correct filter based on the preset
    if (selectedPreset) {
      filters.push(
        <TableFilter
          onSelect={setSelectedUsers}
          options={userOptions}
          initialSelection={[]}
          title={texts.selectAgents}
          key={selectedPreset.id}
        />,
      );
    }

    // Render the coach filter only if we are in the COMMISSION_FULL_DATA tab and there is more than one coach,
    // since the 'All' option is already included in the selection.
    if (selectedTab === commissionsTabs.COMMISSION_FULL_DATA && coaches.length > 1) {
      filters.push(
        <StyledAutoComplete
          defaultValue={ALL}
          onChange={(_, option) => setSelectedCoach(option)}
          options={coaches}
          key={texts.selectCoach}
          renderInput={(params) => <StyledTextField {...params} label={texts.selectCoach} />}
        />,
      );
    }
    return filters;
  }, [
    selectedPreset,
    onSelectedPresetChange,
    userOptions,
    coaches,
    selectedTab,
  ]);

  useEffect(() => {
    // Check if we have to fetch data.
    if (selectedPreset && selectedDate) {
      const { id: dateMonth } = selectedDate;
      const fromDate = moment.utc(dateMonth).startOf('month').format(DateFormat.DEFAULT_DATE_FORMAT);
      const toDate = moment.utc(dateMonth).endOf('month').format(DateFormat.DEFAULT_DATE_FORMAT);
      let payoutReconciliationFunc;
      switch (selectedPreset.id) {
        case Preset.CRX:
        case Preset.CRX_LEAD:
          payoutReconciliationFunc = PayoutReconciliation.getPayoutReconciliationByCRXList;
          break;
        case Preset.INSIDE_SALES:
        case Preset.INSIDE_SALES_LEAD:
          payoutReconciliationFunc = PayoutReconciliation.getPayoutReconciliationBySellerIdList;
          break;
        case Preset.CAM:
          payoutReconciliationFunc = PayoutReconciliation.getPayoutReconciliationByCAMList;
          break;
        case Preset.CPS:
          payoutReconciliationFunc = PayoutReconciliation.getPayoutReconciliationByCPSList;
          break;
        default:
          payoutReconciliationFunc = null;
      }
      if (payoutReconciliationFunc) {
        loadPayoutReconciliationData(
          payoutReconciliationFunc,
          selectedUsers,
          fromDate,
          toDate,
        );
      } else {
        clearData();
      }
    } else {
      clearData();
    }
  }, [
    selectedPreset,
    selectedDate,
    loadPayoutReconciliationData,
    clearData,
    userId,
    selectedUsers,
  ]);

  const columns = useMemo(() => (payoutsConfig ? getColumnsConfig(payoutsConfig, selectedTab) : []), [
    payoutsConfig,
    selectedTab,
  ]);
  // Get the first filtering stage which returns the rows that match the quick search input.
  const { filteredRows: dataRows } = useQuickSearch(payoutReconciliationCollection.docs, columns);
  const rowData = useCalculateCommissionByUser({
    role: selectedPreset?.id,
    selectedUserList: selectedUsers,
    rows: dataRows,
    byUser: selectedTab === commissionsTabs.COMMISSION_BY_USER,
  });

  useEffect(() => {
    if (rowData && selectedTab === commissionsTabs.COMMISSION_FULL_DATA) {
      const filteredCoaches = Object.values(
        rowData.reduce((acc, row) => {
          const { coach: id, coachName: label } = row;

          if (!acc[label]) {
            acc[label] = { id, label };
          }
          return acc;
        }, {}),
      );
      setCoaches([ALL, ...filteredCoaches]);
    }
  }, [rowData, selectedTab]);

  // Filter the rows based on the selected coach
  const filteredRows = useMemo(() => {
    if (selectedCoach === ALL || selectedTab === commissionsTabs.COMMISSION_BY_USER) {
      return rowData;
    }
    return rowData.filter((row) => row.coach === selectedCoach?.id);
  }, [rowData, selectedCoach, selectedTab]);

  const csvFileName = !!selectedPreset && !!selectedDate?.id
    ? `${texts.csvFileNamePrefix}${selectedPreset.id}_${selectedDate?.id}`
    : '';

  const renderCommissionAmount = useCallback(() => {
    if (!selectedPreset || !selectedDate) {
      return null;
    }

    /*
      The leads commissions is calculated as follows:
      - Total commission = Base + IS + AC
      - Lead commission = Base + IS
      - Subtract 65k from the total commission (only for CRX's Leads)
      - Get 1% of that amount
    */
    let commissionAmount = 0;
    // Sum the commission amount
    if (selectedPreset.id === Preset.CRX_LEAD || selectedPreset.id === Preset.INSIDE_SALES_LEAD) {
      const baseCommissionAmount = dataRows.reduce((acc, row) => {
        const {
          feesConfiguration: {
            baseFee,
            insideSalesFee = {
              value: 0,
            },
          },
          commissionableFeeAmount,
          totalCommissionableFeePercentage,
        } = row;

        // Lead fees sum
        const feePercentage = baseFee.value + insideSalesFee.value;

        let amount = 0;
        if (totalCommissionableFeePercentage) {
          // Get the commissionable amount for leads, based on the percentage calculated on leadFeePercetage.
          amount = (feePercentage * commissionableFeeAmount) / totalCommissionableFeePercentage;
        }

        return acc + amount;
      }, 0);

      let leadCommission = 0;
      if (selectedPreset.id === Preset.CRX_LEAD) {
        if (baseCommissionAmount > LEAD_THRESHOLD) {
          leadCommission = baseCommissionAmount - LEAD_THRESHOLD;
        }
      } else {
        // INSIDE_SALES_LEAD will get a commission for the entirety of the base commission.
        leadCommission = baseCommissionAmount;
      }

      // Get 1% of the lead commission
      commissionAmount = leadCommission * LEAD_PERCENTAGE;
    } else {
      commissionAmount = dataRows.reduce((acc, row) => {
        const amount = row[payoutsConfig.commissionNetAmount];
        return acc + amount;
      }, 0);
    }

    const formattedCommission = formatCurrency(commissionAmount, COMMISSION_CURRENCY, MONEY_DECIMALS);

    return <div>{`${texts.commissionAmount} ${formattedCommission}`}</div>;
  }, [dataRows, payoutsConfig, selectedDate, selectedPreset]);

  const availableFilters = useMemo(
    () => (selectedTab === commissionsTabs.COMMISSION_BY_USER
      && (selectedPreset?.id === Preset.INSIDE_SALES_LEAD || selectedPreset?.id === Preset.CRX_LEAD)
      ? filtersArray.filter((filter) => filter.key === 'Preset')
      : filtersArray),
    [filtersArray, selectedPreset, selectedTab],
  );

  const tabContent = useMemo(() => {
    const { component: TabContent, props } = commissionsTabsConfig[selectedTab];
    return (
      <PageContent>
        <TabContent
          {...props}
          rows={filteredRows}
          columns={columns}
          isLoading={isLoadingDocs}
          selectedTab={selectedTab}
          selectedPreset={selectedPreset?.id}
        />
      </PageContent>
    );
  }, [columns, filteredRows, isLoadingDocs, selectedPreset, selectedTab]);

  return (
    <Container>
      <ColoredHeader>
        <TitleRow>
          <div>
            <Title>{texts.title}</Title>
            {!!renderCommissionAmount() && (
              <Description>
                {renderCommissionAmount()}
              </Description>
            )}
          </div>
          <DownloadCSVButton
            disabled={!(columns.map(({ field }) => field).length && csvFileName)}
            rows={filteredRows}
            csvHeaders={columns.map(({ field }) => field)}
            fileName={csvFileName}
          />
        </TitleRow>
        <FilterRow>{availableFilters}</FilterRow>
      </ColoredHeader>

      <StyledTabs tabsConfig={commissionsTabsConfig} selectedTab={selectedTab} onSelectTab={onSelectedTabChange} />
      {tabContent}
    </Container>
  );
};

Commissions.propTypes = {
  userId: PropTypes.string.isRequired,
};

export default compose(withInternalAssignmentsContextProvider, withInternalAssignmentsContextReady)(Commissions);
